/**
 * FeeDisplay — Shows calculated permit fee from /api/v1/permit-fees/calculate
 */
import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Alert, Paper } from '@mui/material';
import { AttachMoney as MoneyIcon } from '@mui/icons-material';

const API_BASE = '/api/v1';

export default function FeeDisplay({ permitType, scope, sqft, jurisdiction }) {
  const [fee, setFee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!permitType && !scope) return;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (permitType) params.set('permitType', permitType);
    if (scope) params.set('scope', scope);
    if (sqft) params.set('sqft', sqft);
    if (jurisdiction) params.set('jurisdiction', jurisdiction);

    fetch(`${API_BASE}/permit-fees/calculate?${params}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.fee) {
          setFee(data.fee);
        } else {
          setError(data.message || 'Could not calculate fee');
        }
      })
      .catch((err) => {
        setError(err.message || 'Failed to fetch fee');
      })
      .finally(() => setLoading(false));
  }, [permitType, scope, sqft, jurisdiction]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Calculating fee...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        Fee estimates vary by jurisdiction. Contact your local permitting office for exact amounts.
      </Alert>
    );
  }

  if (!fee) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'success.50' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <MoneyIcon color="success" />
        <Typography variant="subtitle2" fontWeight={600}>
          Estimated fee
        </Typography>
      </Box>
      <Typography variant="h5" fontWeight={700} color="success.dark">
        ${(fee.total || 0).toFixed(2)}
      </Typography>
      {fee.baseFee != null && (
        <Typography variant="caption" color="text.secondary">
          Base: ${fee.baseFee.toFixed(2)}
          {fee.sqftFee > 0 && ` + $${fee.sqftFee.toFixed(2)} (sqft)`}
        </Typography>
      )}
    </Paper>
  );
}
